import moment from 'moment'

/**
 * Avaliação da obrigatoriedade condicional dos custom fields no preenchimento.
 *
 * Prioridade: a regra de VALOR (requiredWhen) é o portão. Só depois de atendida
 * a condição de valor a DATA (requiredFromDate) é considerada.
 *
 *   obrigatório = requiredField
 *              && (sem requiredWhen OU condição de valor atendida)
 *              && data ativa (sem data => ativa; com data => hoje >= data)
 */

/** Considera um valor como "preenchido". */
export function hasAnswer(value) {
  if (value === undefined || value === null) return false
  if (typeof value === 'string') return value.trim() !== ''
  if (Array.isArray(value)) return value.length > 0
  if (moment.isMoment(value)) return value.isValid()
  return true
}

/** Compara a resposta do campo de origem com o valor esperado, por operador. */
export function operatorMatches(operator, answer, target) {
  // Sem resposta no campo de origem => condição não atendida (qualquer operador).
  if (!hasAnswer(answer)) return false

  const asString = (v) => String(v)
  const asNumber = (v) => Number(v)

  switch (operator) {
    case 'filled':
      return true
    case 'eq':
      return asString(answer) === asString(target)
    case 'neq':
      return asString(answer) !== asString(target)
    case 'contains':
      return Array.isArray(answer)
        ? answer.map(asString).includes(asString(target))
        : asString(answer).includes(asString(target))
    case 'notContains':
      return Array.isArray(answer)
        ? !answer.map(asString).includes(asString(target))
        : !asString(answer).includes(asString(target))
    case 'gt': {
      const a = asNumber(answer)
      const b = asNumber(target)
      return !Number.isNaN(a) && !Number.isNaN(b) && a > b
    }
    case 'lt': {
      const a = asNumber(answer)
      const b = asNumber(target)
      return !Number.isNaN(a) && !Number.isNaN(b) && a < b
    }
    default:
      return false
  }
}

const byGroupOrder = (a, b) =>
  (Number(a.fieldRow) || 0) - (Number(b.fieldRow) || 0) || (Number(a.id) || 0) - (Number(b.id) || 0)

/**
 * Resolve o campo de ORIGEM da regra considerando grupos repetíveis.
 */
export function resolveSourceField(rule, dependentField, fieldsById) {
  if (!rule || rule.fieldId === undefined || rule.fieldId === null) return null

  const original = fieldsById?.[rule.fieldId]
  if (!original) return null

  const dependentGroup = dependentField?.fieldGroup

  if (!original.fieldGroup || !dependentGroup || original.fieldGroup === dependentGroup) {
    return original
  }

  const allFields = Object.values(fieldsById)

  const originGroupFields = allFields.filter((f) => f.fieldGroup === original.fieldGroup).sort(byGroupOrder)
  const position = originGroupFields.findIndex((f) => String(f.id) === String(original.id))
  if (position < 0) return original

  const dependentGroupFields = allFields.filter((f) => f.fieldGroup === dependentGroup).sort(byGroupOrder)
  return dependentGroupFields[position] || original
}

/**
 * A condição de valor (requiredWhen) está atendida dado o estado atual do form?
 * @param {object} rule           a regra requiredWhen
 * @param {object} values         respostas atuais (por internalFieldName)
 * @param {object} fieldsById     mapa id -> campo
 * @param {object} [dependentField] campo dependente, para resolver a origem por instância
 */
export function isConditionMet(rule, values, fieldsById, dependentField) {
  if (!rule || rule.fieldId === undefined || rule.fieldId === null || !rule.operator) {
    return false
  }

  const sourceField = resolveSourceField(rule, dependentField, fieldsById)
  if (!sourceField) return false

  const answer = values?.[sourceField.internalFieldName]
  return operatorMatches(rule.operator, answer, rule.value)
}

/** A obrigatoriedade por data está ativa? (sem data => ativa) */
export function isDateActive(field) {
  if (!field?.requiredFromDate) return true

  const requiredFromDate = moment(field.requiredFromDate, 'YYYY-MM-DD', true)
  if (!requiredFromDate.isValid()) return true

  return moment().startOf('day').isSameOrAfter(requiredFromDate.startOf('day'), 'day')
}

/**
 * Resultado final: o campo deve estar obrigatório agora?
 * @param {object} field       campo dependente (com requiredField/requiredFromDate/requiredWhen)
 * @param {object} values      respostas atuais dos custom fields (por internalFieldName)
 * @param {object} fieldsById  mapa id -> campo, para resolver o campo de origem
 */
export function evaluateRequired(field, values, fieldsById) {
  if (!field?.requiredField) return false

  if (field.requiredWhen) {
    if (!isConditionMet(field.requiredWhen, values, fieldsById, field)) return false
  }

  return isDateActive(field)
}

/**
 * Visibilidade condicional: um campo com requiredWhen só aparece quando a
 * condição é atendida. Sem requiredWhen, é sempre visível por esta regra.
 */
export function isFieldVisible(field, values, fieldsById) {
  if (!field?.requiredWhen) return true
  return isConditionMet(field.requiredWhen, values, fieldsById, field)
}

/** Constrói o mapa id -> campo a partir de uma lista de campos. */
export function buildFieldsById(fields) {
  if (!fields) return {}
  return fields.reduce((acc, field) => {
    if (field && field.id !== undefined && field.id !== null) acc[field.id] = field
    return acc
  }, {})
}
